// /albums — list screen (W3 §6.1).
//
// Backed by GET /api/v1/albums.  Each card shows album cover + name + item
// count; the system 「未分類」 album is rendered automatically first (server
// orders by is_system DESC, updated_at DESC).  Tap a card → /albums/:albumId.
//
// Apple HIG + iOS 26: large-title hero, 2-col grid on phone, `rounded-2xl`
// cards, system chip for the system album.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Camera, Trash2 } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { EmptyState } from "@/components/primitives/EmptyState";
import { LoadingState } from "@/components/primitives/LoadingState";
import { PageContainer, SectionStack } from "@/components/layout";
import { ApiError } from "@/lib/api/pair";
import { useAlbumList, useCreateAlbum, useDeleteAlbum, useUpdateAlbum } from "@/lib/hooks/album";
import { mediaFileUrl } from "@/lib/api/album";
import type { AlbumRead } from "@/lib/api/album";
import { CreateAlbumDialog } from "@/components/albums/CreateAlbumDialog";
import { ActionSheet, deleteAction } from "@/components/system";
import { useLongPress } from "@/lib/gestures/longPress";

// Every write on this screen — 建立 / 重新命名 / 移到垃圾桶 — goes through the
// `useCreateAlbum` / `useUpdateAlbum` / `useDeleteAlbum` hooks in
// `lib/hooks/album.ts`, and those hooks invalidate `queryKeys.dashboard.all` in
// their own `onSuccess`. There is deliberately no second invalidation here: this
// file owns no album write of its own, and the dashboard-invalidation guard keys
// off imported API names precisely so a page that delegates is not asked to
// duplicate the work.

export function AlbumsPage() {
  const { data, isLoading, isError, error } = useAlbumList();
  const createAlbumMut = useCreateAlbum();
  const deleteAlbumMut = useDeleteAlbum();
  const updateAlbumMut = useUpdateAlbum();
  const [createOpen, setCreateOpen] = useState(false);
  const [actionAlbum, setActionAlbum] = useState<AlbumRead | null>(null);
  const [renameAlbum, setRenameAlbum] = useState<AlbumRead | null>(null);
  // W39.53 — 「移到垃圾桶」 used to `console.warn` and return, so the sheet
  // closed and the album was still there with nothing said. A failure now has
  // to be visible, or it is the same silence one layer down.
  const [actionError, setActionError] = useState<string | null>(null);

  const albums = data ?? [];
  // System albums are first by sort; custom albums follow. The pair's
  // 「未分類」 is the only is_system=true row.
  const systemAlbum = albums.find((a) => a.is_system);
  const customAlbums = albums.filter((a) => !a.is_system);

  // `error.message` is safe to show verbatim here: every non-2xx branch in
  // `lib/api/album.ts` now builds its ApiError from the server's documented
  // `error.message` (繁體中文) with `API_REQUEST_FAILED` as the fallback, so it
  // can no longer be `fetch`'s English reason phrase.
  const actionErrorText = (err: unknown): string =>
    err instanceof ApiError ? err.message : "操作未完成，請稍後再試。";

  return (
    <>
      <div data-testid="albums-page">
        <PageHero
          eyebrow="一起的回憶"
          title="相簿"
          subtitle={
            albums.length === 0
              ? "尚未建立任何相簿"
              : // D-1 — the second number is the photo total, summed from each
                // album's own `item_count`. The album detail hero shows the same
                // figure from `itemsTotal` (the server's own count for that one
                // album); here it is an aggregate over the list, so the two are
                // different questions rather than two answers to one.
                `共 ${albums.length} 本 · ${albums.reduce((sum, a) => sum + (a.item_count ?? 0), 0)} 張相片`
          }
          trailing={
            // Per W35.5 spec §2.1 + W35.5 follow-up issue #4: trailing MUST
            // match the anniversary / wish / diary baseline — 44pt circular
            // "+" in `bg-you-strong text-paper`. The previous
            // `text-ink-1 hover:bg-black/5` ghost variant was an outlier.
            //
            // W52.0 D-2 — the bin entry joins it here. It used to be a
            // right-aligned row **under the grid**, which put it below the fold
            // on any account with more than one screen of albums — and the
            // thing it reaches is the undo for 移到垃圾桶, i.e. the control you
            // need precisely when the grid has just shrunk. ADR-0115 names
            // exactly this shape ("一個句子釘在列表底部").
            <div className="flex items-center gap-s-2">
              <Link
                to="/albums/trash"
                data-testid="goto-album-trash"
                aria-label="相簿垃圾桶"
                className="press shell-btn text-ink-2 hover:bg-black/5 transition-colors duration-quick"
              >
                <Trash2 className="w-4 h-4" aria-hidden />
              </Link>
              <button
                type="button"
                aria-label="新增相簿"
                data-testid="albums-add"
                className="press cta-pill hover:opacity-90 transition-opacity"
                onClick={() => setCreateOpen(true)}
              >
                <Plus size={18} aria-hidden />
                新增
              </button>
            </div>
          }
        />

        <PageContainer vertical="default">
          <SectionStack density="default">
            {actionError ? (
              <div
                className="text-body-sm text-partner-strong"
                data-testid="albums-action-error"
                role="alert"
              >
                {actionError}
              </div>
            ) : null}
            {isLoading ? (
              <LoadingState count={4} />
            ) : isError ? (
              <div className="text-body-sm text-partner-strong" data-testid="albums-error">
                {error instanceof ApiError ? error.message : "載入失敗"}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-s-3" data-testid="albums-grid">
                {systemAlbum ? <AlbumCardLink key={systemAlbum.id} album={systemAlbum} /> : null}
                {customAlbums.map((album) => (
                  <AlbumCardButton
                    key={album.id}
                    album={album}
                    onLongPress={() => setActionAlbum(album)}
                  />
                ))}
              </div>
            )}

            {albums.length === 0 && !isLoading ? (
              <EmptyState
                title="還沒有相簿"
                description="建立第一本相簿開始收集回憶"
                actionLabel="新增相簿"
                onAction={() => setCreateOpen(true)}
              />
            ) : null}
          </SectionStack>
        </PageContainer>
      </div>

      <CreateAlbumDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        busy={createAlbumMut.isPending}
        onSubmit={async (values) => {
          await createAlbumMut.mutateAsync({
            name: values.name,
            description: values.description ?? null,
          });
          setCreateOpen(false);
        }}
      />

      <CreateAlbumDialog
        mode="edit"
        open={renameAlbum !== null}
        onOpenChange={(next) => {
          if (!next) setRenameAlbum(null);
        }}
        busy={updateAlbumMut.isPending}
        title="重新命名相簿"
        submitLabel="儲存"
        submitBusyLabel="儲存中…"
        initialName={renameAlbum?.name ?? ""}
        initialDescription={renameAlbum?.description ?? null}
        onSubmit={async (values) => {
          const target = renameAlbum;
          if (!target) return;
          // No local catch: `CreateAlbumDialog` already catches, renders the
          // message, and — because the sheet stays open — leaves the typed name
          // on screen. Re-throwing here to "handle" it would be a no-op wrapper
          // around a `throw` (which `no-useless-catch` correctly refuses).
          await updateAlbumMut.mutateAsync({
            albumId: target.id,
            name: values.name,
            description: values.description,
          });
          setRenameAlbum(null);
          setActionError(null);
        }}
      />

      <ActionSheet
        open={actionAlbum !== null}
        onClose={() => setActionAlbum(null)}
        title={actionAlbum?.name ?? ""}
        actions={
          actionAlbum && !actionAlbum.is_system
            ? [
                deleteAction({
                  onConfirm: () => {
                    const target = actionAlbum;
                    setActionAlbum(null);
                    deleteAlbumMut.mutate(target.id, {
                      onSuccess: () => setActionError(null),
                      onError: (err) => setActionError(actionErrorText(err)),
                    });
                  },
                }),
                {
                  id: "rename",
                  label: "重新命名",
                  onClick: () => {
                    setRenameAlbum(actionAlbum);
                    setActionAlbum(null);
                    setActionError(null);
                  },
                },
              ]
            : []
        }
        testId="albums-action-sheet"
      />
    </>
  );
}

interface AlbumCardLinkProps {
  album: AlbumRead;
}

function AlbumCardLink({ album }: AlbumCardLinkProps) {
  return (
    <Link to={`/albums/${album.id}`} data-testid={`album-card-${album.id}`} className="block press">
      <CardBody album={album} />
    </Link>
  );
}

interface AlbumCardButtonProps {
  album: AlbumRead;
  onLongPress: (album: AlbumRead) => void;
}

function AlbumCardButton({ album, onLongPress }: AlbumCardButtonProps) {
  // W39.93. This was a private `useLongPressSimulator` bound to
  // `onContextMenu` alone, so a real 700 ms press-and-hold on a card measured
  // at 393×852 fell through to the link and **navigated into the album**
  // instead of opening `albums-action-sheet`. The canonical hook owns the
  // gesture now, and the right-click path stays because it already worked.
  const pressAndHold = useLongPress({
    thresholdMs: 250,
    onLongPress: () => onLongPress(album),
  });
  return (
    <Link
      to={`/albums/${album.id}`}
      data-testid={`album-card-${album.id}`}
      className="block press"
      onPointerDown={pressAndHold}
      onContextMenu={(e) => {
        // A right press never starts the hook (it returns early for
        // `button !== 0`), so this cannot double-fire with a long press; and
        // when a long press has fired, the hook has already armed a one-shot
        // `contextmenu` swallow for exactly this event.
        e.preventDefault();
        onLongPress(album);
      }}
    >
      <CardBody album={album} />
    </Link>
  );
}

function CardBody({ album }: { album: AlbumRead }) {
  const cover = album.cover_media;
  // Per W35.5 follow-up issue #8: DB may have Media rows whose actual files
  // were deleted on disk (DB ↔ FS drift). The `<img onError>` handler falls
  // back to the Camera-icon placeholder so the user never sees a broken
  // yellow square.
  const [coverBroken, setCoverBroken] = useState(false);
  const showCover = cover && !coverBroken;
  return (
    <div className="rounded-2xl surface-plain overflow-hidden shadow-card flex flex-col">
      <div className="aspect-square w-full bg-paper-deep relative">
        {showCover ? (
          <img
            src={mediaFileUrl(cover.id)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={() => setCoverBroken(true)}
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center text-paper/80"
            aria-hidden
          >
            <Camera size={36} />
          </div>
        )}
        {album.is_system ? (
          <span
            className="absolute top-2 left-2 rounded-full bg-you/15 px-2 py-0.5 text-body-xs text-you-strong"
            data-testid="system-album-chip"
          >
            未分類
          </span>
        ) : null}
      </div>
      <div className="p-3 flex flex-col gap-1">
        <h3 className="font-display text-title-3 text-ink-1 line-clamp-1">{album.name}</h3>
        <p className="text-body-sm text-ink-3" data-testid="album-item-count">
          {album.item_count} 張相片
        </p>
      </div>
    </div>
  );
}
